import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ProducerInterface from '@app/Interface/ProducerInterface';
import UserAuthentication from '@app/Security/UserAuthentication';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import UserModel from '@app/Model/UserModel';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';
import { ActionGroup, Button, DescriptionList, DescriptionListDescription, DescriptionListGroup, DescriptionListTerm, Form, PageSection, Title } from '@patternfly/react-core';
import ToastNotificationInterface, { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import UserFpuAccessHelper from '@app/Service/UserFpuAccessHelper';
import RevokeUserModel from '@app/Model/RevokeUserModel';
import FpuPermissionsComponentTypeInterface from '@app/Interface/FpuPermissionsComponentViewInterface';
import FpuPermissionsComponentTypeModel from '@app/Model/FpuPermissionsComponentViewModel';
import { PermissionDenied } from '@app/PermissionDenied/PermissionDenied';
import ToastNotificationModel from '@app/Model/ToastNotificationModel';
import User from "@app/Interface/UserInterface";
import ProducerModel from '@app/Model/ProducerModel';

interface FpuRevokeUserPermissionsProps {
    producer: ProducerInterface;
    userId: number;
    userToUpdate: User;
    changeView: (component: FpuPermissionsComponentTypeInterface, fetchUsers?: boolean, toastData?: ToastNotificationInterface) => void;
}

const FpuRevokeUserPermissions: React.FC<FpuRevokeUserPermissionsProps> = ({ producer, userId, userToUpdate, changeView }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const { userState } = React.useContext(UserContext);

    // NOTE: if user permission was changed, they would need to log out/log in to see changes.
    const canViewPage = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, TemplateDefinitions.TEMPLATE_VIEW_FPU_LIST_PAGE);

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        setIsFormProcessing(true);

        let notificationType: ToastNotificationType = ToastNotificationType.Default;

        let toastTitle = "";

        let tastDescription = "";

        const data = new RevokeUserModel();

        data.fpuId = producer.producerId;

        data.userId = Number(userId);

        const responseCode = await UserFpuAccessHelper.revokeUser(data);

        const toastData = new ToastNotificationModel();

        if (responseCode === 200) {

            notificationType = ToastNotificationType.Success;
            toastTitle = "Success";
            tastDescription = "Account was successfully revoked.";
            CreateToastNotification(toastDispatch, notificationType, toastTitle, tastDescription);
            setIsFormProcessing(false);
            changeView(new FpuPermissionsComponentTypeModel(0, new ProducerModel(), 0, new UserModel(), "FpuPermissions"), true);

        } else {

            switch (responseCode) {

                case 200:
                    toastData.type = ToastNotificationType.Success;
                    toastData.title = "Success";
                    toastData.description = "Account was successfully revoked.";
                    changeView(new FpuPermissionsComponentTypeModel(0, new ProducerModel(), 0, new UserModel(), "FpuPermissions"), true, toastData);
                    break;

                case 400:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Invalid input";
                    tastDescription = "Please try again or contact support if the issue persists.";
                    break;

                case 404:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Resource not found";
                    tastDescription = "Endpoint to save user is invalid, try again or contact support.";
                    break;

                case 422:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Bad user data";
                    tastDescription = "Updated data error";
                    break;

                default:
                    notificationType = ToastNotificationType.Warning;
                    toastTitle = "Unknown error";
                    tastDescription = "Unable to revoke user, contact support.";
                    break;

            }

            CreateToastNotification(toastDispatch, notificationType, toastTitle, tastDescription);

            setIsFormProcessing(false);

        }

    }

    React.useEffect(() => {

        return () => {

            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    if (canViewPage) {

        let displayName = "n/a";

        if (userToUpdate.firstName && userToUpdate.lastName) {
            displayName = `${userToUpdate.firstName} ${userToUpdate.lastName}`;
        }

        let userAddress: JSX.Element | null = null;

        if (userToUpdate.address.length && userToUpdate.city.length & userToUpdate.state.length && userToUpdate.zip.length) {

            userAddress = (<div>
                {userToUpdate.address}<br />
                {userToUpdate.address2.length ? <>{userToUpdate.address2}</> : null}
                {userToUpdate.city}, {userToUpdate.state} {userToUpdate.zip}
            </div>);

        }

        return (
            <ToastNotifications>
                <PageSection>
                    <Button
                        variant="primary"
                        onClick={() => changeView(new FpuPermissionsComponentTypeModel(0, new ProducerModel(), 0, new UserModel(), "FpuPermissions"), false)}

                    >
                        Return to User List
                    </Button>
                    <Title headingLevel="h1" className="pf-u-mb-md">Revoke FPU Permissions</Title>
                    <Title headingLevel="h2">{producer.alphaName} - {producer.producerId}</Title>
                    <DescriptionList isHorizontal>
                        <DescriptionListGroup>
                            <DescriptionListTerm>Name</DescriptionListTerm>
                            <DescriptionListDescription>{displayName}</DescriptionListDescription>
                        </DescriptionListGroup>
                        <DescriptionListGroup>
                            <DescriptionListTerm>Email</DescriptionListTerm>
                            <DescriptionListDescription>{userToUpdate.email}</DescriptionListDescription>
                        </DescriptionListGroup>
                        {userAddress !== null &&
                            <DescriptionListGroup>
                                <DescriptionListTerm>Address</DescriptionListTerm>
                                <DescriptionListDescription>{userAddress}</DescriptionListDescription>
                            </DescriptionListGroup>
                        }
                    </DescriptionList>
                    <Form>
                        <ActionGroup>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                spinnerAriaValueText={isFormProcessing ? "Revoking" : undefined}
                                isLoading={isFormProcessing}
                            >{isFormProcessing ? "Revoking" : "Revoke"}</Button>
                        </ActionGroup>
                    </Form>
                </PageSection>
            </ToastNotifications>
        );

    } else {

        return (
            <PermissionDenied />
        );

    }

}

export default FpuRevokeUserPermissions;